import React, { useEffect, useState } from 'react';
import { Page, Layout, Card, Text, BlockStack, Banner, Modal } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import useNotifications from '../hooks/useNotifications';
import ReviewPromptCard from '../components/ReviewPromptCard';
import ReferAndEarnTile from '../components/ReferAndEarnTile';
import useShopify from '../hooks/useShopify';
import { useTranslation } from '../i18n';
import useCurrency from '../hooks/useCurrency';
import { MOBILE_APP_LINKS } from '../data/mobileAppLinks';
import { apiFetch } from '../lib/api';
import { requiredPlanLabel } from '../config/plan-gates';
import ThemeEditorAiCard from '../components/ThemeEditorAiCard';
import EmbedNudgeCard from '../components/EmbedNudgeCard';
import GettingStarted from '../components/GettingStarted';
import OrdersAccessNotice from '../components/OrdersAccessNotice';


// Everything Autonoma can do — each card navigates to the feature.
// Titles/descriptions are i18n keys under dashboard.capabilities.*.
//   route      — clicking navigates there
//   modal      — clicking opens an in-page popup instead of navigating
//   hoverBorder — informational card: keeps the hover border/lift but is
//                 NOT clickable (kicks in automatically with data)
const CAPABILITIES = [
  // No separate "landing pages" feature — the product page is part of
  // Build Products from Scratch (user-facing there's no distinct thing).
  // `feature` keys into config/plan-gates.js — the badge shows the
  // lowest tier that actually has the feature, so badges can't drift
  // from plan-quotas.config.js again.
  { icon: '🏪', key: 'buildProducts', route: '/new-product', feature: 'productBuilds' },
  { icon: '🌐', key: 'fullWebsite', route: '/website', feature: 'websites' },
  { icon: '🖼️', key: 'imageAds', route: '/ads', feature: 'imageAds' },
  { icon: '🎬', key: 'videoAds', route: '/ads', feature: 'videoAds', badgeKey: 'dashboard.badgeNew' },
  { icon: '📰', key: 'advertorials', route: '/advertorials', feature: 'advertorials' },
  { icon: '📱', key: 'releaseAds', route: '/ads', feature: 'publishAds' },
  { icon: '🧪', key: 'splitTesting', route: '/split-testing', feature: 'splitTests' },
  { icon: '📈', key: 'scaling', route: null, hoverBorder: true, feature: 'scalingSuggestions' },
  { icon: '🏆', key: 'winningAds', route: null, hoverBorder: true, feature: 'winningAdDetection' },
  { icon: '💬', key: 'aiAssistant', route: '/chat', feature: 'aiChat' },
  // Autonoma AI inside Shopify's theme editor (app embed) — onboarding modal
  // with the activateAppId deep link + the 6-digit code fallback.
  { icon: '🎨', key: 'themeEditorAi', route: null, modal: 'themeEditorAi', feature: 'aiChat', badgeKey: 'dashboard.badgeNew' },
  { icon: '📲', key: 'mobileApp', route: null, modal: 'mobileApp', feature: 'mobileApp' },
];

/**
 * Popup with the iOS/Android store links for the mobile app, each with
 * an open + copy-link action so merchants can send it to their phone.
 */
function MobileAppModal({ onClose, t }) {
  const [copied, setCopied] = useState(null); // 'ios' | 'android' | null

  const copyLink = async (kind) => {
    const url = MOBILE_APP_LINKS[kind];
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API unavailable (http / permissions) — textarea fallback.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* nothing else to try */ }
      document.body.removeChild(ta);
    }
    setCopied(kind);
    setTimeout(() => setCopied(k => (k === kind ? null : k)), 2000);
  };

  const stores = [
    { kind: 'ios', icon: '', label: t('dashboard.mobileAppModal.ios') },
    { kind: 'android', icon: '🤖', label: t('dashboard.mobileAppModal.android') },
  ];

  const btnBase = {
    border: 'none', borderRadius: 8, padding: '8px 14px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(20, 16, 41, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 460,
          boxShadow: '0 18px 50px rgba(20,16,41,0.25)',
        }}
      >
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              📲 {t('dashboard.mobileAppModal.title')}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t('dashboard.mobileAppModal.subtitle')}
            </Text>
          </BlockStack>

          {stores.map(store => (
            <div
              key={store.kind}
              style={{
                border: '1.5px solid #e4e0f7', borderRadius: 12,
                padding: '14px 16px', background: '#fbfaff',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 22 }} aria-hidden="true">{store.icon}</span>
              <span style={{ flex: 1, fontWeight: 600, fontSize: 14, minWidth: 100 }}>
                {store.label}
              </span>
              <a
                href={MOBILE_APP_LINKS[store.kind]}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...btnBase,
                  background: 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)',
                  color: 'white', textDecoration: 'none',
                }}
              >
                {t('dashboard.mobileAppModal.open')}
              </a>
              <button
                type="button"
                onClick={() => copyLink(store.kind)}
                style={{
                  ...btnBase,
                  background: copied === store.kind ? '#e6f7ee' : '#f5f3ff',
                  color: copied === store.kind ? '#0a7a3d' : '#6B4EE8',
                }}
              >
                {copied === store.kind
                  ? `✓ ${t('dashboard.mobileAppModal.copied')}`
                  : t('dashboard.mobileAppModal.copyLink')}
              </button>
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ ...btnBase, background: 'transparent', color: '#666' }}
            >
              {t('dashboard.mobileAppModal.close')}
            </button>
          </div>
        </BlockStack>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { shop } = useShopify();
  const { notifications, markActioned, markRead, applyLocal } = useNotifications(shop);
  const { symbol } = useCurrency(shop);
  const [metaInsights, setMetaInsights] = useState(null);
  const [metaConnected, setMetaConnected] = useState(null); // null = unknown
  const [showMobileAppModal, setShowMobileAppModal] = useState(false);
  const [showThemeEditorModal, setShowThemeEditorModal] = useState(false);
  // One-time ask: anonymised insights. New shops start ON (pre-checked
  // on plan selection) and decide there, so this banner only reaches
  // shops that predate the plan-picker checkbox and never recorded a
  // decision — their stored value is left alone until they answer.
  // Null = don't show.
  const [consentAsk, setConsentAsk] = useState(null);

  useEffect(() => {
    if (!shop) return;
    apiFetch(`/api/meta/insights`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        setMetaConnected(!!data.connected);
        if (data.connected && data.insights) setMetaInsights(data.insights);
      })
      .catch(() => {});
    apiFetch(`/api/settings/defaults`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data && data.dataSharingAsked === false) setConsentAsk(true);
      })
      .catch(() => {});
  }, [shop]);

  const answerConsent = async (consent) => {
    setConsentAsk(null); // dismiss immediately — the write is best-effort
    try {
      await apiFetch(`/api/settings/data-sharing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ shop, consent }),
      });
    } catch { /* re-asked next visit if the write failed */ }
  };

  // Top 3 unread, unactioned suggestions surface as cards on the dashboard.
  // The app-embed nudge has its own card above (EmbedNudgeCard) — its
  // bell entry must not show up a second time in this list.
  const topSuggestions = notifications
    .filter(n => !n.read && !n.actioned && n.type !== 'embed_nudge')
    .slice(0, 2);

  return (
    <Page
      title={t('dashboard.title')}
      subtitle={t('dashboard.subtitle')}
      primaryAction={{ content: t('dashboard.primaryAction'), onAction: () => navigate('/new-product') }}
    >
      <BlockStack gap="600">
        {/* ── Orders-access notice (only while Shopify is still
            approving order-data access; hides itself once ok) ── */}
        <OrdersAccessNotice shop={shop} />
        {/* ── Theme-editor embed nudge (top of the suggestion area;
            renders nothing when hidden/done) ───────────── */}
        <EmbedNudgeCard />
        {/* ── Getting started: the four steps of the core journey, with
            the next one highlighted (presentation only — reads the
            existing generations list + Meta connection state). ── */}
        <GettingStarted shop={shop} metaConnected={metaConnected} />
        {/* ── One-time data-sharing consent ask ─────────── */}
        {consentAsk && (
          <Banner
            title="Share anonymised insights to help improve Autonoma"
            tone="info"
            action={{ content: 'Share anonymised insights', onAction: () => answerConsent(true) }}
            secondaryAction={{ content: 'No thanks', onAction: () => answerConsent(false) }}
          >
            <p>
              Share anonymised, aggregated usage insights to help improve Autonoma.
              No personal data is ever shared — never your customer data, product
              names, sales numbers, or store identity. On by default for new stores
              (you can turn it off any time in Settings); your store hasn&apos;t chosen yet.
            </p>
          </Banner>
        )}
        {/* ── AI Suggestions (top element) ─────────────── */}
        {topSuggestions.length > 0 && (
          <BlockStack gap="300">
            <span className="au-eyebrow">{t('dashboard.suggestions')}</span>
            <BlockStack gap="200">
              {topSuggestions.map(notif => (notif.type === 'review_prompt' ? (
                // App Store review prompt — the 👍 👎 satisfaction gate
                // at the success moment that fired it.
                <div className="au-suggestion" key={notif.id}>
                  <span className="au-suggestion__icon" aria-hidden="true">
                    {notif.icon || '⭐'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <ReviewPromptCard notif={notif} variant="card" onLocalUpdate={applyLocal} />
                  </div>
                </div>
              ) : (
                <div className="au-suggestion" key={notif.id}>
                  <span className="au-suggestion__icon" aria-hidden="true">
                    {notif.icon || '✨'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div className="au-suggestion__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {notif.title}
                      {/* Data-engine confidence indicator: hover shows
                          whether this is niche-data-backed or framework */}
                      {notif.dataConfidence && (
                        <span
                          title={notif.dataConfidence.label}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, cursor: 'help' }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                              background: notif.dataConfidence.level === 'high'
                                ? 'linear-gradient(135deg, #6B4EE8 0%, #2eb872 100%)'
                                : notif.dataConfidence.level === 'medium'
                                  ? 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)'
                                  : '#c9cbd4',
                            }}
                          />
                          {/* Text twin of the colour so the level isn't colour-only */}
                          <span style={{ fontSize: 10, color: '#6d7175', textTransform: 'capitalize' }}>
                            {notif.dataConfidence.level === 'high' ? 'High confidence'
                              : notif.dataConfidence.level === 'medium' ? 'Medium confidence' : 'Framework'}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="au-suggestion__msg">{notif.message}</div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
                      {notif.execution?.kind === 'chat_prompt' ? (
                        // "Let's do it": hands the suggestion to the AI
                        // chat operator, which executes it end-to-end
                        // (confirm-first — the merchant reviews before
                        // anything runs). Replaces the route button.
                        <button
                          type="button"
                          className="au-suggestion__cta"
                          onClick={async () => {
                            // Actioned at click time so it never
                            // re-appears (backend re-marks on chat
                            // create — idempotent belt-and-braces).
                            await markActioned(notif.id);
                            navigate(`/chat?fromSuggestion=${notif.id}`);
                          }}
                        >
                          Let&rsquo;s do it ✨
                        </button>
                      ) : notif.action && (
                        // Informational / legacy suggestions keep the
                        // original route button.
                        <button
                          type="button"
                          className="au-suggestion__cta"
                          onClick={async () => {
                            await markActioned(notif.id);
                            const route = notif.action.route;
                            if (!route) return;
                            // External http(s) targets (e.g. Meta Ads
                            // Manager after publishing) open in a new tab.
                            if (/^https?:\/\//i.test(route)) window.open(route, '_blank', 'noopener');
                            else navigate(route);
                          }}
                        >
                          {notif.action.label} →
                        </button>
                      )}
                      <button
                        type="button"
                        className="au-suggestion__dismiss"
                        onClick={() => markRead(notif.id)}
                      >
                        <span>{t('dashboard.dismiss')}</span>
                      </button>
                    </div>
                    {notif.execution?.kind === 'chat_prompt' && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#6d7175' }}>
                        This opens your AI chat and does it for you — you review before it runs.
                      </div>
                    )}
                  </div>
                </div>
              )))}
            </BlockStack>
          </BlockStack>
        )}

        {/* ── Meta performance (when connected) ─────────── */}
        {metaInsights && (
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">{t('dashboard.adPerformance')}</Text>
              <Layout>
                {[
                  { label: t('dashboard.metrics.totalSpend'), value: `${symbol}${metaInsights.spend}` },
                  { label: t('dashboard.metrics.roas'), value: `${metaInsights.roas}x` },
                  { label: t('dashboard.metrics.purchases'), value: String(metaInsights.purchases) },
                  { label: t('dashboard.metrics.revenue'), value: `${symbol}${metaInsights.revenue}` },
                  { label: t('dashboard.metrics.clicks'), value: Number(metaInsights.clicks || 0).toLocaleString() },
                  { label: t('dashboard.metrics.ctr'), value: `${metaInsights.ctr}%` },
                ].map(stat => (
                  <Layout.Section key={stat.label} variant="oneThird">
                    <BlockStack gap="100">
                      <Text as="p" variant="headingLg">{stat.value}</Text>
                      <Text as="p" variant="bodySm" tone="subdued">{stat.label}</Text>
                    </BlockStack>
                  </Layout.Section>
                ))}
              </Layout>
            </BlockStack>
          </Card>
        )}

        {/* ── Capabilities showcase ────────────────────── */}
        <BlockStack gap="300">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">{t('dashboard.capabilitiesTitle')}</Text>
          <Text as="p" variant="bodySm" tone="subdued">{t('dashboard.capabilitiesSubtitle')}</Text>
        </BlockStack>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px',
          }}
        >
          {CAPABILITIES.map((cap, i) => {
            const clickable = !!cap.route || !!cap.modal;
            const activate = () => {
              if (cap.route) navigate(cap.route);
              else if (cap.modal === 'mobileApp') setShowMobileAppModal(true);
              else if (cap.modal === 'themeEditorAi') setShowThemeEditorModal(true);
            };
            return (
            <div
              key={i}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={() => clickable && activate()}
              onKeyDown={e => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  activate();
                }
              }}
              style={{
                background: 'white',
                border: '1px solid #E3E3E3',
                borderRadius: '14px',
                padding: '20px',
                cursor: clickable ? 'pointer' : 'default',
                transition: 'all 0.2s',
                position: 'relative',
              }}
              onMouseEnter={e => {
                // Only clickable cards lift on hover — informational
                // cards (automatic features) must not look like buttons.
                if (clickable) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(107,78,232,0.15)';
                  e.currentTarget.style.borderColor = '#6B4EE8';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = '#E3E3E3';
              }}
            >
              {cap.badgeKey && (
                <span
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '700',
                    padding: '3px 8px',
                    borderRadius: '6px',
                  }}
                >
                  {t(cap.badgeKey)}
                </span>
              )}
              <div style={{ fontSize: '32px', marginBottom: '12px' }} aria-hidden="true">
                {cap.icon}
              </div>
              <Text as="h3" variant="headingMd">{t(`dashboard.capabilities.${cap.key}.title`)}</Text>
              <div style={{ marginTop: '6px', marginBottom: '12px' }}>
                <Text as="p" variant="bodySm" tone="subdued">{t(`dashboard.capabilities.${cap.key}.desc`)}</Text>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#6B4EE8',
                    background: '#f5f3ff',
                    padding: '3px 10px',
                    borderRadius: '6px',
                  }}
                >
                  {requiredPlanLabel(cap.feature)}+
                </span>
                {cap.hoverBorder && (
                  <span className="au-status" title={t('dashboard.automaticHint')}>{t('dashboard.automatic')}</span>
                )}
                {clickable && (
                  <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600, color: '#6B4EE8' }}>{t('common.open')} →</span>
                )}
              </div>
            </div>
            );
          })}
        </div>
        </BlockStack>

        {/* ── Refer & Earn tile (peer referral; two-path after a discovery trigger) ── */}
        <ReferAndEarnTile shop={shop} />

        {showMobileAppModal && (
          <MobileAppModal onClose={() => setShowMobileAppModal(false)} t={t} />
        )}
        {showThemeEditorModal && (
          <Modal open onClose={() => setShowThemeEditorModal(false)} title={t('dashboard.capabilities.themeEditorAi.title')} secondaryActions={[{ content: t('dashboard.mobileAppModal.close'), onAction: () => setShowThemeEditorModal(false) }]}>
            <Modal.Section>
              <div data-testid="theme-editor-ai-modal">
                <ThemeEditorAiCard />
              </div>
            </Modal.Section>
          </Modal>
        )}

      </BlockStack>
    </Page>
  );
}
